/**
 * The "preview city" every collection surface needs.
 *
 * WHY THIS EXISTS AT ALL: all 15 live rows in `search_guides` have `city_id IS NULL`, which on a
 * city-scoped kind means "follow the READER's city". "A night out in {city}" is ONE row that
 * renders a different list in every city, so it has no size, no cover and no preview until you
 * name a city. Verified on production 2026-08-23: `select city_id from search_guides where
 * is_active` returns NULL for all 15.
 *
 * TWO SOURCES, ON PURPOSE:
 *   • `admin_guide_preview_cities()` (migration 20260871000000, UNAPPLIED as of 2026-08-23) gives
 *     the cities with at least 4 photo-bearing, rated venues — i.e. the only cities where a
 *     collection can actually fill. It does NOT return coordinates.
 *   • `cities` gives `lat` / `lng`, which `get_search_guides(p_lat, p_lng, …)` needs to resolve
 *     the reader's city. `cities` uses `lat`/`lng`; `venues` uses the long form `latitude`/
 *     `longitude` — mixing them up returns the wrong city silently.
 *
 * So: read coordinates from `cities` (one permissive public SELECT policy, `cities_read_public` —
 * the same read `CityHeroPickerModal` relies on), then narrow and rank by the RPC's pool when the
 * RPC exists. Before the migration the pool is unknown, the list falls back to rating volume, and
 * every caller still works.
 *
 * `total_shado_ratings` is one of the additive columns `CityOverviews` guards against being
 * absent: PostgREST fails the WHOLE select if one column is missing, hence the bare-columns retry.
 */
import { supabase } from './supabase';

export type PreviewCity = {
  id: string;
  name: string;
  lat: number | null;
  lng: number | null;
  /** Photo-bearing rated venues in the city — 0 means "not known", not "none". */
  pool: number;
};

/**
 * Shared with `CollectionsScreen`, deliberately: switching city on the Search page's preview and
 * then opening the Collections table should be looking at the same city. Both surfaces read the
 * key on mount and the pill strip remounts them, so the choice carries across.
 */
export const PREVIEW_CITY_STORAGE_KEY = 'admin:collections:previewCity';

export function readStoredPreviewCity(): string {
  try {
    return window.localStorage.getItem(PREVIEW_CITY_STORAGE_KEY) ?? '';
  } catch {
    return '';
  }
}

export function writeStoredPreviewCity(id: string): void {
  try {
    if (id) window.localStorage.setItem(PREVIEW_CITY_STORAGE_KEY, id);
    else window.localStorage.removeItem(PREVIEW_CITY_STORAGE_KEY);
  } catch {
    /* private window / blocked site data — the selector still works for this session */
  }
}

type CityRow = { id: string; name: string | null; lat: number | null; lng: number | null };

async function readCityCoordinates(): Promise<CityRow[]> {
  const ranked = await supabase
    .from('cities')
    .select('id, name, lat, lng, total_shado_ratings')
    .not('lat', 'is', null)
    .order('total_shado_ratings', { ascending: false, nullsFirst: false })
    .limit(200);
  if (!ranked.error) return (ranked.data ?? []) as CityRow[];

  const bare = await supabase.from('cities').select('id, name, lat, lng').not('lat', 'is', null).limit(200);
  if (bare.error) throw new Error(bare.error.message);
  return (bare.data ?? []) as CityRow[];
}

/**
 * Cities worth previewing, best first. Never throws for a missing RPC — only for a failed
 * `cities` read, which would mean the selector genuinely cannot be built.
 */
export async function loadPreviewCities(): Promise<PreviewCity[]> {
  const coords = await readCityCoordinates();
  const byId = new Map<string, CityRow>();
  coords.forEach((c) => {
    if (c.id && c.name) byId.set(c.id, c);
  });

  const { data, error } = await supabase.rpc('admin_guide_preview_cities');
  if (!error && Array.isArray(data)) {
    const pooled = (data as { id?: string; name?: string; pool?: number }[])
      .map((p) => {
        const c = p.id ? byId.get(p.id) : undefined;
        if (!c) return null; /* no coordinates -> get_search_guides could not resolve it anyway */
        return {
          id: c.id,
          name: c.name as string,
          lat: c.lat,
          lng: c.lng,
          pool: typeof p.pool === 'number' ? p.pool : 0,
        };
      })
      .filter((c): c is PreviewCity => c !== null);
    if (pooled.length > 0) return pooled.sort((a, b) => b.pool - a.pool || a.name.localeCompare(b.name));
  }

  /* Pre-migration: keep the `cities` order (rating volume), pool unknown. */
  return coords
    .filter((c) => c.id && c.name)
    .slice(0, 60)
    .map((c) => ({ id: c.id, name: c.name as string, lat: c.lat, lng: c.lng, pool: 0 }));
}
